import React from 'react';
import { ShieldCheck, Award, Clock, Star, Headphones, BadgeCheck } from 'lucide-react';
import { useLocation } from 'react-router-dom';

/**
 * TrustBar, premium credibility strip injected globally on every public sub-page,
 * just above the footer. Mimics ubreakifix's "Authorized Service Provider" trust strip
 * but uses semantic trust signals (warranty / reviews / experience) since 2A is not
 * an official OEM-authorized provider yet.
 *
 * Hidden on:
 *   - homepage `/` (homepage has its own dark hero + chat widget identity)
 *   - admin / store / employee-portal / webdesign routes
 */
const TrustBar = () => {
  const location = useLocation();
  const path = location.pathname;
  const skip =
    path === '/' ||
    path.startsWith('/admin') ||
    path.startsWith('/store') ||
    path.startsWith('/employee-portal') ||
    path.startsWith('/webdesign');
  if (skip) return null;

  const items = [
    {
      icon: Star,
      title: '4.9 / 5',
      subtitle: '470 Google reviews',
      iconColor: 'text-amber-500',
      iconBg: 'bg-amber-50',
      iconRing: 'ring-amber-100',
    },
    {
      icon: ShieldCheck,
      title: '30-Day Warranty',
      subtitle: 'On every repair',
      iconColor: 'text-emerald-600',
      iconBg: 'bg-emerald-50',
      iconRing: 'ring-emerald-100',
    },
    {
      icon: Clock,
      title: 'Same-Day Service',
      subtitle: 'Most repairs in 30 min',
      iconColor: 'text-cyan-600',
      iconBg: 'bg-cyan-50',
      iconRing: 'ring-cyan-100',
    },
    {
      icon: BadgeCheck,
      title: 'Premium Parts',
      subtitle: 'OEM-grade only',
      iconColor: 'text-blue-600',
      iconBg: 'bg-blue-50',
      iconRing: 'ring-blue-100',
    },
    {
      icon: Award,
      title: 'Est. 2025',
      subtitle: '24,500+ repairs done',
      iconColor: 'text-rose-600',
      iconBg: 'bg-rose-50',
      iconRing: 'ring-rose-100',
    },
    {
      icon: Headphones,
      title: 'Real Humans',
      subtitle: 'No phone trees',
      iconColor: 'text-violet-600',
      iconBg: 'bg-violet-50',
      iconRing: 'ring-violet-100',
    },
  ];

  return (
    <section
      className="relative bg-white border-y border-slate-200/70"
      aria-label="Why customers trust 2A Electronics"
      data-testid="global-trust-bar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <div className="text-center mb-8 sm:mb-10">
          <p
            className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500 mb-2"
            data-no-style
          >
            Why Chicago Trusts 2A
          </p>
          <h2
            className="text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900 tracking-tight"
            style={{ letterSpacing: '-0.025em' }}
          >
            Repairs done right. Backed by real people.
          </h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 sm:gap-5">
          {items.map((it, idx) => (
            <div
              key={idx}
              className="group flex flex-col items-center text-center px-3 py-5 rounded-2xl border border-slate-200/80 bg-white hover:border-slate-300 hover:shadow-[0_10px_30px_-10px_rgba(15,23,42,0.12)] transition-all duration-200"
              data-testid={`trust-item-${idx}`}
            >
              <div
                className={`flex items-center justify-center w-12 h-12 rounded-xl ${it.iconBg} ring-4 ${it.iconRing} mb-3 group-hover:scale-105 transition-transform`}
              >
                <it.icon className={`w-5 h-5 ${it.iconColor}`} strokeWidth={2.4} />
              </div>
              <div className="font-bold text-slate-900 text-sm sm:text-base leading-tight">
                {it.title}
              </div>
              <div className="text-xs text-slate-500 mt-1 leading-snug">{it.subtitle}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TrustBar;
